import { useCallback, useEffect, useState } from 'react';
import { callZigzagFunction, compareWithAppVersion } from '@croquiscom/web2app';
import { useAtom } from 'jotai';

import {
  BEAUTY_MAKEUP_CATEGORY,
  DESCRIPTION_MIN_HEIGHT,
  productDetailVisibleAtom,
  useCatalogProductIdParams,
  usePdpBaseInfo,
} from '@domains/pdp-common';

interface Props {
  sizeRecommendRef: HTMLDivElement | null;
}
export const useTabDescriptionService = ({ sizeRecommendRef }: Props) => {
  const param = useCatalogProductIdParams();
  const {
    product,
    product: { description },
  } = usePdpBaseInfo(param);
  const isBeautyCategory = Boolean(product.category_key === BEAUTY_MAKEUP_CATEGORY.key);

  const [isRefreshed, setIsRefreshed] = useState(false);
  const [isDescriptionOpened, setIsDescriptionOpened] = useState(false);
  const [wrapperRect, setWrapperRect] = useState({ top: 0, height: 0 });
  const [productDetailVisible, setProductDetailVisible] = useAtom(productDetailVisibleAtom);

  useEffect(() => {
    if (!sessionStorage) {
      return;
    }
    const is_loggedin = sessionStorage.getItem(`size_recommend_login?${product.id}`);
    const input_body_info = sessionStorage.getItem(`size_recommend_body_info?${product.id}`);
    setIsRefreshed(Boolean(is_loggedin || input_body_info));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isRefreshed && sizeRecommendRef && wrapperRect.height > DESCRIPTION_MIN_HEIGHT) {
      setTimeout(() => {
        sizeRecommendRef.scrollIntoView({
          block: 'center',
        });
      }, 500);
      setIsRefreshed(false);
      sessionStorage.removeItem(`size_recommend_body_info?${product.id}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wrapperRect, isRefreshed, sizeRecommendRef]);

  const wrapperRef = useCallback(
    (node) => {
      if (node !== null) {
        const { top, height } = node.getBoundingClientRect();
        setWrapperRect({ top, height });
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [description, isDescriptionOpened],
  );

  const intersectionObserverCallback = (entry: IntersectionObserverEntry) => {
    if (entry.isIntersecting && !productDetailVisible) {
      setProductDetailVisible(true);

      if (compareWithAppVersion('7.75.0')) {
        callZigzagFunction('productDetailVisible')({ isVisible: true });
      }
    }

    if (
      !entry.isIntersecting &&
      productDetailVisible &&
      // MEMO: 상품 본문 하단부터는 본문을 벗어나도 앱 인터페이스 호출하지 않도록 함.
      entry.boundingClientRect.height > Math.abs(entry.boundingClientRect.y)
    ) {
      setProductDetailVisible(false);

      if (compareWithAppVersion('7.75.0')) {
        callZigzagFunction('productDetailVisible')({ isVisible: false });
      }
    }
  };

  return {
    catalogProductId: param.catalog_product_id,
    wrapperRect,
    wrapperRef,
    isBeautyCategory,
    isDescriptionOpened,
    setIsDescriptionOpened,
    intersectionObserverCallback,
  };
};
